// ── Regroupement des plans de la page Mes projets ──────────
// Convention de nommage : « Projet — plan vN (badge) »
//   Maison Bérard — garage double v10      → projet « Maison Bérard », plan « Garage double », v10
//   Maison Bérard — plan architecte (plan préféré) → badge « plan préféré »
// Chaque plan garde sa carte ; le badge est le texte final entre parentheses.

export interface PlanName {
  /** Projet (avant le tiret) ; nom complet s'il n'y a pas de tiret */
  group: string;
  /** Plan, majuscule initiale */
  title: string;
  /** Numero de version */
  version: number | null;
  /** Libelle de la version : « v5 », « v5 meublée » */
  versionLabel: string | null;
  /** Texte final entre parentheses */
  tag: string | null;
}

const capitalize = (s: string) => (s ? s[0].toLocaleUpperCase('fr') + s.slice(1) : s);

export function parsePlanName(name: string): PlanName {
  let rest = name.trim();
  let tag: string | null = null;
  const tagMatch = rest.match(/^(.*\S)\s*\(([^()]+)\)$/);
  if (tagMatch) { rest = tagMatch[1]; tag = tagMatch[2].trim(); }

  let group = rest;
  const sep = rest.match(/^(.+?)\s+[—–-]\s+(.+)$/);
  if (sep) { group = sep[1].trim(); rest = sep[2].trim(); }

  let version: number | null = null;
  let versionLabel: string | null = null;
  const v = rest.match(/^(.*?)\s+v(\d+)\b(.*)$/i);
  if (v && v[1]) {
    version = Number(v[2]);
    versionLabel = `v${v[2]}${v[3].trimEnd()}`;
    rest = v[1];
  }
  return { group, title: capitalize(rest), version, versionLabel, tag };
}

/** Comparaison sans casse ni accents (regroupement, recherche) */
export const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

interface Plan { id: string; name: string; updatedAt: string }

export interface PlanEntry<T extends Plan> {
  project: T;
  /** Plan sans le nom du projet ni le badge : « Garage double v10 » */
  title: string;
  tag: string | null;
}

export interface ProjectGroup<T extends Plan> {
  key: string;
  name: string;
  plans: PlanEntry<T>[];
  updatedAt: string;
}

/** Entree d'affichage d'un plan (titre sans le projet, badge a part) */
export function planEntry<T extends Plan>(project: T): PlanEntry<T> {
  const n = parsePlanName(project.name);
  return { project, title: n.versionLabel ? `${n.title} ${n.versionLabel}` : n.title, tag: n.tag };
}

/** Projets → groupes (par projet), plans et groupes tries du plus recent au plus ancien */
export function groupProjects<T extends Plan>(projects: T[]): ProjectGroup<T>[] {
  const groups = new Map<string, ProjectGroup<T>>();
  for (const p of [...projects].sort((a, b) => (b.updatedAt > a.updatedAt ? 1 : -1))) {
    const key = normalize(parsePlanName(p.name).group);
    if (!groups.has(key)) groups.set(key, { key, name: parsePlanName(p.name).group, plans: [], updatedAt: p.updatedAt });
    groups.get(key)!.plans.push(planEntry(p));
  }
  return [...groups.values()];
}

/** « il y a quelques minutes », « il y a 3 h », « il y a 12 j », puis la date */
export function formatAgo(dateStr: string, now = Date.now()): string {
  const diff = now - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 1) return 'il y a quelques minutes';
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days} j`;
  return `le ${new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}
